import {useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'shared/reload/helper/useNavigate';
import {useProductContext} from 'contexts/ProductContext';
import {useJobContext} from 'contexts';
import {PartialRoom} from 'shared/types/PartialRoom';
import {ItemType} from 'shared/components/Product/Item';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {
    matchPath,
    useLocation,
    useParams,
    useSearchParams,
} from 'react-router-dom';

export const useLinkFormatter = (item: ItemType, isProduct: boolean) => {
    const {jobId} = useParams();
    const [searchParams] = useSearchParams();
    const category = searchParams.get('category');
    const subCategory = searchParams.get('subCategory');
    const favourites = searchParams.get('favourites');
    const showAll = searchParams.get('products');

    const location = useLocation();

    const getLink = useCallback(() => {
        const params: {
            category?: string;
            subCategory?: string;
            favourites?: string;
            product?: string;
            sundry?: string;
            products?: string;
        } = {};

        if (category) params.category = category;
        if (subCategory) params.subCategory = subCategory;
        if (favourites) params.favourites = '1';
        if (showAll) params.products = showAll;

        if (item.id && isProduct) params.product = String(item.id);
        else params.sundry = String(item.id);

        if (params.hasOwnProperty('sundry')) {
            // get category from sundry item
            params.category = String(item.sundryCategoryId);
        }

        const query = new URLSearchParams(params).toString();

        if (isProduct) {
            let currentUrl = location.pathname;
            const favouritesPage = matchPath(
                '/v2/job/:jobId/room/:roomId/favourites',
                currentUrl
            );
            if (favouritesPage) {
                currentUrl = currentUrl.replace('favourites', 'product');
            }
            return `${currentUrl}?${query}`;
        }

        return `/v2/job/${jobId}/hardware?${query}`;
    }, [
        item,
        isProduct,
        category,
        subCategory,
        favourites,
        showAll,
        jobId,
        location,
    ]);

    return {
        getLink,
    };
};

export const useItem = (item: ItemType, isProduct: boolean) => {
    const {AddToFavourite: addToFavourite} = useProductContext();
    const {room} = useJobContext() as {room: PartialRoom};

    const [isFavourite, setIsFavourite] = useState(item.favourites > 0);
    const [favouriteButtonDisabled, setFavouriteButtonDisabled] =
        useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {setSelectedCabinetGroup} = useRoomPlannerContext();

    const {getLink} = useLinkFormatter(item, isProduct);

    const addToFavouriteHandler = useCallback(
        async (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            setLoading(true);

            setFavouriteButtonDisabled(true);
            if (addToFavourite) {
                await addToFavourite(item, !isFavourite, isProduct);
            }

            setIsFavourite((isFavourite) => !isFavourite);
            setFavouriteButtonDisabled(false);
            setLoading(false);
        },
        [setFavouriteButtonDisabled, addToFavourite, isFavourite]
    );

    const itemClickHandler = useCallback(() => {
        setSelectedCabinetGroup?.(null);
        navigate(getLink());
    }, [navigate, getLink, setSelectedCabinetGroup]);

    const style = useMemo(() => {
        return isProduct
            ? {
                  backgroundImage: `url("/uploads/gocabinet_materials/${
                      room && room.extMaterial && room.extMaterial.image != ''
                          ? room.extMaterial.image
                          : 'default_exterior.jpg'
                  }")`,
              }
            : {};
    }, [isProduct, room]);

    return {
        loading,
        isFavourite,
        style,
        favouriteButtonDisabled,
        itemClickHandler,
        addToFavouriteHandler,
    };
};
